import React, { useState, useEffect } from 'react';
import {
  LINE_STYLE_COLORS,
  STYLEABLE_LINE_TYPES,
  DEFAULT_LINE_STYLES,
} from '../../config/lineStyles';

const LABELS = {
  speech: 'Speech',
  embodied: 'Embodied',
  gloss: 'Gloss',
  translation: 'Translation',
};

// Render text in the editor's font so the label doubles as a faithful preview.
const PREVIEW_FONT = "'Courier New', Courier, monospace";

const LineStylesDialog = ({ currentStyles, multilingual, onConfirm, onCancel }) => {
  // Working copy — only committed to the app on OK.
  const [styles, setStyles] = useState(currentStyles);

  // Speech and embodied always; gloss and translation only when multilingual
  // is enabled, mirroring how the app reveals those line types elsewhere.
  const rows = STYLEABLE_LINE_TYPES.filter(
    (type) => multilingual || (type !== 'gloss' && type !== 'translation')
  );

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const update = (type, patch) => {
    setStyles((prev) => ({ ...prev, [type]: { ...prev[type], ...patch } }));
  };

  const restoreDefaults = () => setStyles(DEFAULT_LINE_STYLES);

  const previewStyle = (type) => {
    const s = styles[type];
    return {
      fontFamily: PREVIEW_FONT,
      fontWeight: s.bold ? 'bold' : 'normal',
      fontStyle: s.italic ? 'italic' : 'normal',
      color: s.color === 'default' ? 'inherit' : s.color,
    };
  };

  return (
    <div className="dialog-overlay" onClick={onCancel}>
      <div
        className="dialog-card dialog-card-wide"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="dialog-title">Line Styles</h3>

        <div className="line-styles-grid" role="table">
          <div className="line-styles-row line-styles-head" role="row">
            <span className="line-styles-label-head" />
            <span>Bold</span>
            <span>Italic</span>
            <span className="line-styles-colour-head">Colour</span>
          </div>

          {rows.map((type) => {
            const s = styles[type];
            return (
              <div className="line-styles-row" role="row" key={type}>
                <span className="line-styles-label" style={previewStyle(type)}>
                  {LABELS[type]}
                </span>

                <label className="line-styles-check">
                  <input
                    type="checkbox"
                    checked={s.bold}
                    onChange={(e) => update(type, { bold: e.target.checked })}
                    aria-label={`${LABELS[type]} bold`}
                  />
                </label>

                <label className="line-styles-check">
                  <input
                    type="checkbox"
                    checked={s.italic}
                    onChange={(e) => update(type, { italic: e.target.checked })}
                    aria-label={`${LABELS[type]} italic`}
                  />
                </label>

                <div className="line-styles-swatches">
                  {LINE_STYLE_COLORS.map((c) => (
                    <button
                      type="button"
                      key={c.value}
                      className={
                        'line-styles-swatch' +
                        (s.color === c.value ? ' selected' : '')
                      }
                      style={{ backgroundColor: c.swatch }}
                      title={c.label}
                      aria-label={`${LABELS[type]} colour: ${c.label}`}
                      aria-pressed={s.color === c.value}
                      onClick={() => update(type, { color: c.value })}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="dialog-actions line-styles-actions">
          <button
            type="button"
            className="dialog-button"
            onClick={restoreDefaults}
          >
            Restore defaults
          </button>
          <span className="line-styles-actions-spacer" />
          <button type="button" className="dialog-button" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="dialog-button dialog-button-primary"
            onClick={() => onConfirm(styles)}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default LineStylesDialog;
